import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { submitPasswordReset, verifyResetPasswordToken } from '../api/client';
import CelestialLayout from '../components/celestial/CelestialLayout';
import TopNav from '../components/celestial/TopNav';
import MaterialIcon from '../components/celestial/MaterialIcon';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setVerifyError('Bağlantıda token bulunamadı. E-postadaki şifre sıfırlama linkini kullanın.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await verifyResetPasswordToken(token);
        if (cancelled) return;
        if (res.valid) {
          setTokenValid(true);
          setEmail(res.email?.trim() ? res.email.trim() : null);
        }
      } catch (e) {
        if (cancelled) return;
        setVerifyError(e instanceof Error ? e.message : 'Token doğrulanamadı');
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    if (password.length < 8) {
      setSubmitError('Şifre en az 8 karakter olmalıdır');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Şifreler eşleşmiyor');
      return;
    }
    setLoading(true);
    try {
      await submitPasswordReset(token, password);
      setDone(true);
      setTimeout(() => navigate('/giris'), 2500);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Şifre güncellenemedi');
    } finally {
      setLoading(false);
    }
  }

  return (
    <CelestialLayout orbs>
      <TopNav />
      <main className="celestial-main celestial-auth">
        <div className="celestial-glass celestial-glass--strong celestial-auth__card">
          <div className="celestial-auth__inner">
            <h1 className="celestial-headline-lg">Şifre sıfırla</h1>

            {verifying && (
              <p className="celestial-body-lg" style={{ fontSize: '0.95rem' }}>
                Bağlantı kontrol ediliyor…
              </p>
            )}

            {!verifying && verifyError && (
              <>
                <div className="celestial-error">{verifyError}</div>
                <p style={{ marginTop: '1rem' }}>
                  <Link to="/giris" className="celestial-btn-ghost">Giriş sayfasına dön</Link>
                </p>
              </>
            )}

            {!verifying && !verifyError && tokenValid && !done && (
              <>
                <p className="celestial-body-lg" style={{ fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                  {email ? (
                    <>
                      Hesap: <strong style={{ color: 'var(--on-surface)' }}>{email}</strong>
                      <br />
                    </>
                  ) : null}
                  Yeni şifrenizi belirleyin.
                </p>
                <form onSubmit={handleSubmit}>
                  {submitError && <div className="celestial-error">{submitError}</div>}
                  <div className="celestial-form-group">
                    <label className="celestial-label" htmlFor="np">Yeni şifre</label>
                    <input
                      id="np"
                      className="celestial-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="celestial-form-group">
                    <label className="celestial-label" htmlFor="npc">Yeni şifre (tekrar)</label>
                    <input
                      id="npc"
                      className="celestial-input"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                  </div>
                  <button
                    type="submit"
                    className="celestial-btn-primary"
                    disabled={loading}
                    style={{ width: '100%' }}
                  >
                    {loading ? 'Kaydediliyor…' : 'Şifreyi güncelle'}
                  </button>
                </form>
              </>
            )}

            {done && (
              <p className="celestial-body-lg" style={{ color: '#22c55e' }}>
                Şifreniz güncellendi. Giriş sayfasına yönlendiriliyorsunuz…
              </p>
            )}

            <p style={{ marginTop: '1.5rem' }}>
              <Link to="/giris" className="celestial-btn-ghost">
                <MaterialIcon name="arrow_back" size={18} />
                Girişe dön
              </Link>
            </p>
          </div>
        </div>
      </main>
    </CelestialLayout>
  );
}
